import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles, getArticlesByCategory, type Article } from '@/mocks/articles';
import { siteConfig } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'xuanhuan');
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const activeCategory = categories.find((c) => c.slug === activeSlug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(getArticlesByCategory(activeSlug));
  }, [activeSlug]);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '分类', item: `${siteConfig.url}/category` },
      { '@type': 'ListItem', position: 3, name: activeCategory?.name || '', item: `${siteConfig.url}/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category/xuanhuan" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{activeCategory?.name}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {activeCategory?.name}小说
            </h1>
            <p className="mt-2 text-foreground-600 text-sm">
              {activeCategory?.description} · 共 {activeCategory?.count?.toLocaleString()} 部作品
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-4 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="bg-background-50 rounded-xl overflow-hidden border border-background-200/70 group cursor-pointer hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.cover}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${article.title} - po18官网${activeCategory?.name}小说`}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-100 text-accent-700">
                        {activeCategory?.name}
                      </span>
                      <span className="text-xs text-accent-600">
                        <i className="ri-star-fill text-accent-500 mr-0.5"></i>
                        {article.rating}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mb-3">{article.author} · {article.readers}人在读</p>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-book-2-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无小说，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}